import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.array.filter.js';
import 'core-js/modules/es.array.is-array.js';
import 'core-js/modules/es.array.reduce.js';
import 'core-js/modules/es.array.some.js';
import 'core-js/modules/es.object.assign.js';
import 'core-js/modules/es.object.to-string.js';
import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef } from 'react';
import ReactSelect from 'react-select';
import classNames from 'classnames';
import { useConfigContextLoose } from '../config-provider/config-provider.js';
import { SELECT_ALL_VALUE, CheckboxOptions, Group, DropdownIndicator, Control, IndicatorSeparator, ClearIndicator, MenuList, Option } from './select-components.js';

/**
 * Select
 * =============================================================================
 * @deprecated
 * The select can be used to show a user a list of options of which they can
 * select one, or multiple.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/experimental-wip-select--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * const options = [
 *   { value: 'casa', label: 'Casandra Page' },
 *   { value: 'dean', label: 'Deana Rowe', disabled: true },
 *   { value: 'dudl', label: 'Dudley Perkins' },
 * ];
 *
 * <Select
 *   value={value}
 *   options={options}
 *   onChange={setValue}
 * />
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param disabled                  - Disable the select.
 * @param disableTyping             - Disables typing in the input field.
 * @param dropdownRender            - Replace the default dropdown renderer for a custom one.
 * @param icon                      - Add an icon to the button.
 * @param isMulti                   - Allow Multiselect
 * @param onChange                  - Default javascript onChange event.
 * @param options                   - The options that will be shown in the menu.
 * @param placeholderSelectElement  - Show a specified element instead of the values.
 * @param selectAllLabel            - Replace the text of the "Select all" button.
 * @param showCheckbox              - Show a checkbox in front of every option in the menu.
 * @param showSelectedItemCount     - Show the count of selected items.
 * @param theme                     - Change the theme of the select component.
 * @param title                     - Show a pre-fix before the selected values.
 * @param value                     - The current value of the select component.
 * @param width                     - The width of the Select (in Pixels).
 * @param label                     - Add label above the input field - recommended to be used in conjunction with "inputId"
 * @param inputId                   - Passed on to ReactSelect component - recommended when using "label"
 * @param menuPortalTarget          - Passed on to ReactSelect component
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/experimental-wip-select--base)
 */
var Select = function Select(props) {
  var disabled = props.disabled,
    disableTyping = props.disableTyping,
    dropdownRender = props.dropdownRender,
    _props$enableSelectAl = props.enableSelectAll,
    enableSelectAll = _props$enableSelectAl === undefined ? false : _props$enableSelectAl,
    _props$hideSelectedOp = props.hideSelectedOptions,
    hideSelectedOptions = _props$hideSelectedOp === undefined ? false : _props$hideSelectedOp,
    icon = props.icon,
    menuHeader = props.menuHeader,
    menuFooter = props.menuFooter,
    _props$menuPlacement = props.menuPlacement,
    menuPlacement = _props$menuPlacement === undefined ? 'auto' : _props$menuPlacement,
    onChange = props.onChange,
    options = props.options,
    placeholderSelectElement = props.placeholderSelectElement,
    _props$selectAllLabel = props.selectAllLabel,
    selectAllLabel = _props$selectAllLabel === undefined ? 'Select all' : _props$selectAllLabel,
    SelectAllLabel = props.SelectAllLabel,
    _props$showCheckbox = props.showCheckbox,
    showCheckbox = _props$showCheckbox === undefined ? false : _props$showCheckbox,
    _props$showCustomChec = props.showCustomCheckbox,
    showCustomCheckbox = _props$showCustomChec === undefined ? false : _props$showCustomChec,
    _props$showSelectedIt = props.showSelectedItemCount,
    showSelectedItemCount = _props$showSelectedIt === undefined ? false : _props$showSelectedIt,
    styles = props.styles,
    theme = props.theme,
    title = props.title,
    _props$value = props.value,
    value = _props$value === undefined ? [] : _props$value,
    width = props.width,
    isMulti = props.isMulti,
    hideDividerForSelectAll = props.hideDividerForSelectAll,
    selectableGroups = props.selectableGroups,
    placeholderElement = props.placeholderElement,
    className = props.className,
    label = props.label,
    inputId = props.inputId,
    menuPortalTarget = props.menuPortalTarget,
    rest = __rest(props, ["disabled", "disableTyping", "dropdownRender", "enableSelectAll", "hideSelectedOptions", "icon", "menuHeader", "menuFooter", "menuPlacement", "onChange", "options", "placeholderSelectElement", "selectAllLabel", "SelectAllLabel", "showCheckbox", "showCustomCheckbox", "showSelectedItemCount", "styles", "theme", "title", "value", "width", "isMulti", "hideDividerForSelectAll", "selectableGroups", "placeholderElement", "className", "label", "inputId", "menuPortalTarget"]);
  var _useConfigContextLoos = useConfigContextLoose(),
    getOverlayContainer = _useConfigContextLoos.getOverlayContainer;
  if (label && !inputId) {
    // eslint-disable-next-line no-console
    console.warn('It\'s recommended to use the "inputId" prop when using the "label" prop');
  }
  var valueArray = Array.isArray(value) ? value : [value];
  // Temporary fix until SelectAllLabel is deprecated
  var tmpSelectAllLabel = SelectAllLabel || selectAllLabel;
  // Need to reset React-Select styles in order to override them with .scss
  var customStyles = Object.assign({
    control: function control() {
      return {};
    },
    option: function option() {
      return {};
    },
    multiValueRemove: function multiValueRemove() {
      return {};
    },
    menuPortal: function menuPortal(base) {
      return Object.assign(Object.assign({}, base), {
        zIndex: undefined
      });
    }
  }, styles);
  var valueRef = useRef(valueArray);
  valueRef.current = valueArray;
  var getAllItemsIncludingSubItems = function getAllItemsIncludingSubItems(list) {
    return list.reduce(function (acc, item) {
      if (item.options && item.options.length) {
        var subItems = item.options.filter(function (subItem) {
          return subItem.value;
        });
        return [].concat(acc, subItems);
      }
      return [].concat(acc, [item]);
    }, []);
  };
  var getArray = function getArray(list) {
    if (Array.isArray(list)) return list;
    return [];
  };
  var isAllSelected = function isAllSelected() {
    var allItems = getAllItemsIncludingSubItems(getArray(valueRef.current));
    var allOptions = getAllItemsIncludingSubItems(options);
    return allItems.length === allOptions.length;
  };
  var isAnySelected = function isAnySelected() {
    return getAllItemsIncludingSubItems(getArray(valueRef.current)).length > 0;
  };
  var isOptionSelected = function isOptionSelected(option) {
    return getAllItemsIncludingSubItems(getArray(valueRef.current)).some(function (item) {
      return item.value === option.value;
    });
  };
  var isOptionDisabled = function isOptionDisabled(option) {
    return option.disabled || false;
  };
  var selectAllOption = {
    value: SELECT_ALL_VALUE,
    label: tmpSelectAllLabel,
    divider: !hideDividerForSelectAll,
    isAllSelected: isAllSelected,
    isAnySelected: isAnySelected
  };
  // common component for all select boxes
  var components = {
    DropdownIndicator: DropdownIndicator,
    Control: Control,
    IndicatorSeparator: IndicatorSeparator,
    ClearIndicator: ClearIndicator,
    MenuList: MenuList,
    Option: Option
  };
  var getComponents = function getComponents() {
    var componentOptions = components;
    // to show the custom checkbox alongside option label
    if (showCheckbox || showCustomCheckbox) {
      componentOptions = Object.assign(Object.assign({}, components), {
        Option: CheckboxOptions
      });
    }
    // To show the count value for selected items alongside placeholder text
    if (showSelectedItemCount) componentOptions = Object.assign(Object.assign({}, componentOptions), {
      MultiValue: function MultiValue(_ref) {
        var getValue = _ref.getValue,
          index = _ref.index;
        var _a;
        return !index && "Selected Items " + ((_a = getValue()) === null || _a === undefined ? undefined : _a.length);
      }
    });
    // To show a custom, user passed component when anything is selected
    if (placeholderSelectElement) componentOptions = Object.assign(Object.assign({}, componentOptions), {
      MultiValue: function MultiValue(_ref2) {
        var index = _ref2.index;
        return !index && placeholderSelectElement;
      }
    });
    if (selectableGroups) {
      componentOptions = Object.assign(Object.assign({}, componentOptions), {
        Group: Group
      });
    }
    if (placeholderElement) {
      componentOptions = Object.assign(Object.assign({}, componentOptions), {
        Placeholder: function Placeholder() {
          return placeholderElement;
        }
      });
    }
    return componentOptions;
  };
  var handleOnChange = function handleOnChange(selected, actionMeta) {
    var action = actionMeta.action,
      option = actionMeta.option;
    if (action === 'select-option' && (option === null || option === undefined ? undefined : option.value) === selectAllOption.value) {
      if (selectAllOption.isAllSelected && !selectAllOption.isAllSelected()) {
        // if select all then pass all options
        onChange(options, actionMeta);
      } else {
        // if de-select all then pass [] list
        onChange([], actionMeta);
      }
    }
    // if an option is unchecked then remove(filter) it from the exising list and pass
    else if (actionMeta.action === 'deselect-option') {
      var itemList = getAllItemsIncludingSubItems(getArray(value));
      var result = itemList.filter(function (item) {
        return item.value !== (option === null || option === undefined ? undefined : option.value) && item.value !== selectAllOption.value;
      });
      onChange(result, actionMeta);
      // if option is selected, then pass the selected item/s
    } else {
      onChange(selected || [], actionMeta);
    }
  };
  return jsxs("div", {
    className: classNames('cogs-v10', 'cogs-select', className, theme),
    style: {
      width: width
    },
    children: [label && jsx("label", {
      htmlFor: inputId,
      className: "cogs-v10 cogs-select__label",
      children: label
    }), jsx(ReactSelect, Object.assign({
      isDisabled: disabled,
      closeMenuOnSelect: !isMulti,
      hideSelectedOptions: hideSelectedOptions,
      isOptionSelected: isOptionSelected,
      isOptionDisabled: isOptionDisabled,
      components: getComponents(),
      menuPortalTarget: menuPortalTarget !== null && menuPortalTarget !== undefined ? menuPortalTarget : getOverlayContainer === null || getOverlayContainer === undefined ? undefined : getOverlayContainer(),
      menuPlacement: menuPlacement,
      className: classNames('cogs-v10', 'cogs-select--container', disableTyping && 'cogs-select--disable-typing', disabled && 'cogs-select--disabled'),
      menuHeader: menuHeader,
      menuFooter: menuFooter,
      classNamePrefix: "cogs-v10 cogs-select",
      styles: customStyles,
      options: enableSelectAll ? [selectAllOption].concat(options) : options,
      icon: icon,
      dropdownRender: dropdownRender,
      title: title,
      value: value && valueArray,
      isSearchable: !disableTyping,
      onChange: handleOnChange,
      isMulti: isMulti,
      tabIndex: disabled ? -1 : rest.tabIndex
    }, inputId ? {
      inputId: inputId
    } : null, rest, {
      onKeyDown: function onKeyDown(ev) {
        var _a;
        if (ev.key === 'Escape') ev.stopPropagation();
        (_a = rest.onKeyDown) === null || _a === undefined ? undefined : _a.call(rest, ev);
      }
    }))]
  });
};

export { Select };
